import { ChangeDetectionStrategy, Component, effect, inject, input, output, untracked } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { PaginationFooterTemplateComponent } from '@connect-services/client/components/pagination-footer-template/pagination-footer-template.component';
import { AbstractLayersTableComponent } from '@connect-services/client/components/project-settings/project-layers-settings/abstract-layers-table.component';
import { CustomColumn, CustomTableComponent } from '@connect-services/src/lib/components/table/table.component';
import { LayersService } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CustomTableComponent,
        PaginationFooterTemplateComponent,
        LoadingComponent,
        NoDataToDisplayComponent,
        RessourcesModule,
    ],
    selector: 'app-flux-layers-table',
    styleUrl: '../project-layers-settings.component.scss',
    templateUrl: './flux-layers-table.component.html',
})
export class FluxLayersTableComponent extends AbstractLayersTableComponent {
    public readonly searchValue = input<string>('');
    public updateFluxItems = output<number>();

    protected tableColumns: CustomColumn[] = [
        {
            key: 'name',
            label: 'layer',
        },
        {
            key: 'type',
            label: 'type',
        },
        {
            key: 'order',
            label: 'order',
        },
        {
            key: 'isVisible',
            label: 'visibility',
        },
    ];

    private readonly layersServiceSdk = inject(LayersService);

    public constructor() {
        super();

        effect(() => {
            if (!this.projectBloc.value()) {
                return;
            }

            this.searchValue();
            this.layerService.forceLayerListRefresh();

            untracked(() => this.loadLayers());
        });
    }

    protected override async loadLayers(): Promise<void> {
        this.isLoading.set(true);

        const loadedLayers = await firstValueFrom(
            this.layersServiceSdk.layerControllerGet({
                project: this.projectBloc.value()!.id,
                searchValue: this.searchValue(),
                types: [this.LayerTypeEnum.WFS, this.LayerTypeEnum.WMS],
                ...this.paginationParameters(),
            }),
        );

        this.paginationParameters.update((parameters) => {
            return {
                ...parameters,
                hasNextPage: loadedLayers.hasNextPage,
                hasPreviousPage: loadedLayers.hasPreviousPage,
                page: loadedLayers.page,
                totalItems: loadedLayers.totalItems,
                totalPages: loadedLayers.totalPages,
            };
        });

        this.layers.set(loadedLayers!.items);

        this.updateFluxItems.emit(this.paginationParameters().totalItems ?? 0);
        this.isLoading.set(false);
    }
}
